import BaseDecimal from 'decimal.js';
import Locale from './Locale';

/**
 * ---
 * category: utilities/i18n
 * ---
 * A wrapper for [decimal.js](https://github.com/MikeMcl/decimal.js)
 * with additional internationalization utilties.
 *
 * @module Decimal
 */

export var Decimal = BaseDecimal.clone();
Decimal.set({
  precision: 100,
  toExpPos: 100
});
var delimiters = {
  // Intl does not support Icelandic for internationalization
  'is-IS': {
    decimal: ',',
    thousands: '.'
  },
  is: {
    decimal: ',',
    thousands: '.'
  }
};
Decimal.getDelimiters = function () {
  var locale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Locale.browserLocale();
  if (Decimal.accountSettingDelimiters && Decimal.accountSettingDelimiters.decimal && Decimal.accountSettingDelimiters.thousands) {
    // If the account has set custom delimiters, use those
    delimiters[locale] = {
      decimal: Decimal.accountSettingDelimiters.decimal,
      thousands: Decimal.accountSettingDelimiters.thousands
    };
  }
  if (!delimiters[locale]) {
    var numberIntl = new Intl.NumberFormat(locale);
    delimiters[locale] = {
      decimal: numberIntl.format(0.5).replace(/[0-9]/g, '')[0],
      thousands: numberIntl.format(1000000).replace(/[0-9]/g, '')[0]
    };
  }
  return delimiters[locale];
};

/**
 * Return a Decimal instance parsed from a string input + locale
 * @param {String|Number|Decimal} input
 * @param {String} locale
 * @returns {Decimal} The number as a Decimal instance
 */
Decimal.parse = function (input) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Locale.browserLocale();
  var value = _parse(input, locale);
  var decimal;
  try {
    decimal = new Decimal(value);
  } catch (_unused) {
    decimal = new Decimal(NaN);
  }
  decimal.locale = locale;
  return decimal;
};
Decimal.toLocaleString = function (string) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Locale.browserLocale();
  var decimal = Decimal(string);
  var precision = string.replace(/.*?(\.|$)/, '').length;
  return decimal.toFixed(precision, locale);
};
Decimal.toLocaleStringIfValid = function (value, locale) {
  if (!value || typeof value !== 'string' || !locale || Decimal.parse(value).isNaN()) return value;
  return Decimal.toLocaleString(value, locale);
};

/**
 * Return a number as a localized, formatted string
 * @param {String} locale
 * @returns {String} The number as a localized/formatted string
 */
Decimal.prototype.toLocaleString = function () {
  var locale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.locale;
  return _format(this.toString(), locale);
};
var toFixed = Decimal.prototype.toFixed;
Decimal.prototype.toFixed = _localizePrecision(toFixed);
var toPrecision = Decimal.prototype.toPrecision;
Decimal.prototype.toPrecision = _localizePrecision(toPrecision);
export default Decimal;

// Convert a localized number string into something that can be passed to the
// Decimal constructor
function _parse(input) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Locale.browserLocale();
  if (typeof input !== 'string' && !(input instanceof String)) return input;
  var spaceCharacterVariants = [' ',
  // U+0020 SPACE
  ' ',
  // U+202F NARROW NO-BREAK SPACE
  ' ' // U+00A0 NO-BREAK SPACE
  ];
  var _Decimal$getDelimiter = Decimal.getDelimiters(locale),
    thousands = _Decimal$getDelimiter.thousands,
    decimal = _Decimal$getDelimiter.decimal;
  var standardizedInput = input;

  // For locales that use space as a thousands separator, accept any variety of space
  if (spaceCharacterVariants.includes(thousands)) {
    spaceCharacterVariants.forEach(function (variant) {
      standardizedInput = standardizedInput.replace(new RegExp("\\".concat(variant), 'g'), '');
    });
  }
  var parsedString = standardizedInput.replace(new RegExp("\\".concat(thousands), 'g'), '') // strip thousands separator
  .replace(new RegExp("\\".concat(decimal), 'g'), '.'); // replace decimal separator with dot

  // Handle the special case of -0, converting it to 0
  if (parsedString === '-0') {
    return '0';
  }
  return parsedString;
}

// Formats the given number string with thousands separators.
// Doesn't take into account Chinese and Indian locales with non-standard grouping
function _format(input) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Locale.browserLocale();
  var _Decimal$getDelimiter2 = Decimal.getDelimiters(locale),
    thousands = _Decimal$getDelimiter2.thousands,
    decimal = _Decimal$getDelimiter2.decimal;
  var isNegative = input[0] === '-';
  var parts = input.split('.');
  var thousandSections = [];
  var curr = parts[0].replace(/^-/, '');
  while (curr.length > 0) {
    thousandSections.unshift(curr.substr(Math.max(0, curr.length - 3), 3));
    curr = curr.substr(0, curr.length - 3);
  }
  var result = thousandSections.join(thousands);
  if (parts[1]) {
    result = "".concat(result).concat(decimal).concat(parts[1]);
  }
  if (isNegative && result) {
    result = "-".concat(result);
  }
  return result;
}
function _localizePrecision(fn) {
  return function (precision, rounding, locale) {
    /* eslint-disable no-param-reassign */
    if (locale == null && typeof rounding === 'string' || rounding instanceof String) {
      locale = rounding;
      rounding = void 0;
    }
    /* eslint-enable no-param-reassign */
    var value = fn.call(this, precision, rounding);

    // Convert exponential notation to decimal
    if (value.indexOf('e') !== -1) value = Number(value).toString();
    if (locale == null) return value;
    return _format(value, locale);
  };
}